/**
 * A list row that can be swiped sideways on a touch screen or trackpad (TRUS-759/760). See
 * useSwipeActions for the gesture rules. The coloured panel behind the row
 * names the action for the side being swiped; it is aria-hidden because every
 * swipe action also has a visible button or menu item on the row. Which action
 * each side runs is the caller's (from swipeSettings, TRUS-761).
 */
import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSwipeActions } from './useSwipeActions';
import { SENSITIVITY_RATIO, useSwipeSettings } from './swipeSettings';

export type SwipeTone = 'pin' | 'read' | 'archive' | 'reply';

export interface SwipeAction {
  label: string;
  icon: LucideIcon;
  tone: SwipeTone;
  onCommit: () => void;
}

const TONE_CLASS: Record<SwipeTone, string> = {
  pin: 'bg-amber-500 text-white',
  read: 'bg-sky-600 text-white',
  archive: 'bg-slate-600 text-white',
  reply: 'bg-transparent text-muted-foreground',
};

function prefersReducedMotion(): boolean {
  try {
    return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export interface SwipeRowProps {
  /** Shown, and run, when the row is swiped toward the right. */
  right?: SwipeAction;
  /** Shown, and run, when the row is swiped toward the left. */
  left?: SwipeAction;
  disabled?: boolean;
  className?: string;
  /** Background of the moving content: it must be opaque to hide the panel. */
  contentClassName?: string;
  /** Props for the outer element (role, data-testid, ...). */
  outerProps?: React.HTMLAttributes<HTMLDivElement> & Record<`data-${string}`, string | undefined>;
  children: React.ReactNode;
}

export function SwipeRow({ right, left, disabled, className, contentClassName = 'bg-white dark:bg-slate-950', outerProps, children }: SwipeRowProps) {
  // TRUS-761: how far a swipe must go, and whether a trackpad swipes, are the user's settings.
  const settings = useSwipeSettings();
  const { ref, state, handlers } = useSwipeActions({
    onSwipeRight: right?.onCommit,
    onSwipeLeft: left?.onCommit,
    disabled,
    commitRatio: SENSITIVITY_RATIO[settings.sensitivity],
    trackpad: settings.trackpad,
    trackpadReverse: settings.trackpadReverse,
  });
  const action = state.side === 'right' ? right : state.side === 'left' ? left : undefined;
  const Icon = action?.icon;
  const reduced = prefersReducedMotion();
  const swipeable = !disabled && (!!right || !!left);

  return (
    <div
      {...outerProps}
      ref={ref}
      className={cn('relative overflow-hidden', className, outerProps?.className)}
      // Vertical pans stay with the browser (scrolling); horizontal ones are ours.
      style={swipeable ? { touchAction: 'pan-y', ...outerProps?.style } : outerProps?.style}
      {...(swipeable ? handlers : {})}
    >
      {action && Icon && (
        <div
          aria-hidden="true"
          data-testid="swipe-panel"
          data-swipe-side={state.side ?? undefined}
          className={cn(
            'pointer-events-none absolute inset-0 flex items-center gap-2 px-4 text-sm font-semibold',
            TONE_CLASS[action.tone],
            state.side === 'right' ? 'justify-start' : 'justify-end',
          )}
        >
          <Icon className={cn('h-5 w-5 transition-transform', state.armed && !reduced && 'scale-125')} />
          {action.tone !== 'reply' && <span>{action.label}</span>}
        </div>
      )}
      <div
        className={cn('relative', contentClassName)}
        style={{
          transform: state.offset ? `translate3d(${state.offset}px,0,0)` : undefined,
          // Follow the finger exactly while dragging; spring back when released.
          transition: state.dragging || reduced ? 'none' : 'transform 180ms ease-out',
        }}
      >
        {children}
      </div>
    </div>
  );
}

export default SwipeRow;
